import { IAnswer } from '@/services/FormStructure';

function isLocalStorageAccessError(error: unknown): boolean {
    return error instanceof DOMException && (error.name === 'SecurityError' || error.name === 'QuotaExceededError');
}

export class memoryStorage {
    private static memStore: Record<string, string> = {};

    static clear(): void {
        memoryStorage.memStore = {};
        try {
            localStorage.clear();
        } catch (error) {
            if (!isLocalStorageAccessError(error)) throw error;
        }
    }

    static getItem(key: string): string | null {
        if (!memoryStorage.memStore[key]) {
            try {
                const localStorageItem = localStorage.getItem(key);
                if (localStorageItem) {
                    memoryStorage.memStore[key] = localStorageItem;
                }
            } catch (error) {
                if (!isLocalStorageAccessError(error)) throw error;
            }
        }
        return memoryStorage.memStore[key] || null;
    }

    static setItem(key: string, value: string): void {
        memoryStorage.memStore[key] = String(value);
        try {
            localStorage.setItem(key, String(value));
        } catch (error) {
            if (!isLocalStorageAccessError(error)) throw error;
        }
    }

    static removeItem(key: string): void {
        delete memoryStorage.memStore[key];
        try {
            localStorage.removeItem(key);
        } catch (error) {
            if (!isLocalStorageAccessError(error)) throw error;
        }
    }
}

export const setAnswer = (transactionId: number | string, answer: IAnswer[], formQuestionId: number, formId: number): void => {
    let answers: IAnswer[][] = null;

    // Doesn't have any cached answer yet? Will create here;
    if (!getAnswers(transactionId, formId)) {
        setAnswers(transactionId, [answer], formId);
        return;
    }

    answers = JSON.parse(JSON.stringify(getAnswers(transactionId, formId)));
    const answersList = new Set(answer.map(item => item.formQuestionId));
    const alreadyAnswerThatQuestion = answers.some((el: IAnswer[]) => answersList.has(el[0]?.formQuestionId));

    // Remove all cached answers when the question was clear by the user
    const haveAnyInvalidAnswer = answer.every((item: IAnswer): boolean => item.answer === null && item?.text === null);

    if (haveAnyInvalidAnswer) {
        answers = answers.filter((item: IAnswer[]) => {
            // Create a list with only the formQuestion Id and make sure doen't have duplicate id
            const questionAnswered = new Set(item.map((el: IAnswer) => el.formQuestionId));
            // Return only question that have valid answer
            return !questionAnswered.has(formQuestionId);
        });
        setAnswers(transactionId, answers, formId);
        return;
    }

    // If already Answer the question will update the answer
    if (alreadyAnswerThatQuestion) {
        answers = updateAnswerValue(transactionId, answer, formId);
        setAnswers(transactionId, answers, formId);
        return;
    }

    // Handle with multiple answers for ButtonSelectComponent and Autocomplete
    answers.push(answer);
    answers.forEach((item: IAnswer[], index: number) => {
        const multipleAnswerQuestionHasChange = answers.some((el: IAnswer[]) => answer[0]?.formQuestionId === el[0]?.formQuestionId && item[0]?.formQuestionId === el[0]?.formQuestionId);
        if (multipleAnswerQuestionHasChange) {
            const answerStorage: IAnswer[] = JSON.parse(JSON.stringify(answer));
            if (Object.keys(answerStorage[0]).includes('answer')) {
                answers[index] = answer;
            } else {
                item.forEach((answerItem: IAnswer, idx: number) => {
                    if (answerItem.formQuestionId === answerStorage[0].formQuestionId && answerItem.answer === undefined) {
                        item.splice(idx, 1);
                    }
                });
            }
        }
    });
    setAnswers(transactionId, answers, formId);
};

function updateAnswerValue(transactionId: number | string, answer: IAnswer[], formId: number): IAnswer[][] {
    const localAnswers: IAnswer[][] = JSON.parse(JSON.stringify(getAnswers(transactionId, formId)));
    const answers = localAnswers.map((el: IAnswer[]) => {
        if (el[0]?.formQuestionId === answer[0]?.formQuestionId) return answer;
        return el;
    });
    return answers;
}

export const setAnswers = (transactionId: number | string, answers: IAnswer[][], formId: number): void => {
    memoryStorage.setItem(`answers-tid${transactionId}-fid${formId}`, JSON.stringify(answers));
};

export const getAnswers = (transactionId: number | string, formId: number): IAnswer[][] | undefined => {
    const getDataByTransactionId: IAnswer[][] | undefined = JSON.parse(memoryStorage.getItem(`answers-tid${transactionId}`));
    if (getDataByTransactionId) return getDataByTransactionId;
    return JSON.parse(memoryStorage.getItem(`answers-tid${transactionId}-fid${formId}`));
};

export const removeData = (transactionId: number | string, formId: number): void => {
    memoryStorage.removeItem(`answers-tid${transactionId}-fid${formId}`);
    memoryStorage.removeItem(`page-tid${transactionId}-fid${formId}`);
    memoryStorage.removeItem(`answers-tid${transactionId}`);
    memoryStorage.removeItem(`page-tid${transactionId}`);
};
